export default function QuickStart() {
  return (
    <div className="prose prose-blue max-w-none">
      <h1>Quick Start Guide</h1>

      <p className="text-xl text-gray-600">
        Get started with redgeGate API in 5 minutes.
      </p>

      <h2>Step 1: Get Your API Key</h2>
      <ol>
        <li>Sign up or log in to your <a href="/dashboard">redgeGate Dashboard</a></li>
        <li>Navigate to <a href="/dashboard/settings">Settings → API Keys</a></li>
        <li>Click "Generate New API Key"</li>
        <li>Copy and securely store your API key</li>
      </ol>

      <div className="not-prose bg-red-50 border-l-4 border-red-600 p-4 my-6">
        <p className="text-sm text-red-900">
          <strong>Important:</strong> Keep your API key secret. Never commit it to version control or expose it in client-side code.
        </p>
      </div>

      <h2>Step 2: Set Up Your Device</h2>
      <ol>
        <li>Download the redgeGate Android app from the Play Store</li>
        <li>Open the app and scan the QR code from your dashboard</li>
        <li>Grant required permissions (SMS, Phone, Notifications)</li>
        <li>Wait for the device to appear in your dashboard as "Online"</li>
      </ol>

      <h2>Step 3: Send Your First SMS</h2>

      <p>Choose your preferred programming language:</p>

      <h3>Using cURL</h3>
      <div className="not-prose bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
        <pre className="text-sm overflow-x-auto">
{`curl https://api.textredge.com/api/v1/sms/send \\
  -X POST \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+1234567890",
    "message": "Hello from redgeGate!"
  }'`}
        </pre>
      </div>

      <h3>Using JavaScript (Node.js)</h3>
      <div className="not-prose bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
        <pre className="text-sm overflow-x-auto">
{`const axios = require('axios');

const apiKey = 'your_api_key_here';
const baseURL = 'https://api.textredge.com/api/v1';

async function sendSMS(to, message) {
  const response = await axios.post(\`\${baseURL}/sms/send\`, {
    to,
    message
  }, {
    headers: {
      'X-API-Key': apiKey,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
}

sendSMS('+1234567890', 'Hello from redgeGate!')
  .then(result => console.log(result))
  .catch(err => console.error(err));`}
        </pre>
      </div>

      <h3>Using Python</h3>
      <div className="not-prose bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
        <pre className="text-sm overflow-x-auto">
{`import requests

API_KEY = 'your_api_key_here'
BASE_URL = 'https://api.textredge.com/api/v1'

def send_sms(to, message):
    headers = {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json'
    }

    payload = {
        'to': to,
        'message': message
    }

    response = requests.post(
        f'{BASE_URL}/sms/send',
        json=payload,
        headers=headers
    )
    return response.json()

result = send_sms('+1234567890', 'Hello from redgeGate!')
print(result)`}
        </pre>
      </div>

      <h2>Step 4: Verify the Response</h2>

      <p>If successful, you'll receive a response like this:</p>

      <div className="not-prose bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
        <pre className="text-sm overflow-x-auto">
{`{
  "success": true,
  "data": {
    "smsId": "msg_1a2b3c4d5e6f",
    "status": "queued",
    "recipient": "+1234567890",
    "message": "Hello from redgeGate!",
    "createdAt": "2024-01-15T10:30:00.000Z"
  }
}`}
        </pre>
      </div>

      <p>The message status will be:</p>
      <ul>
        <li><code>queued</code> - Message accepted and queued</li>
        <li><code>sent</code> - Message sent to your device</li>
        <li><code>delivered</code> - Message delivered to recipient</li>
        <li><code>failed</code> - Message delivery failed</li>
      </ul>

      <h2>Step 5: Track Your Messages</h2>

      <p>You can retrieve message status and history:</p>

      <div className="not-prose bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
        <pre className="text-sm overflow-x-auto">
{`curl https://api.textredge.com/api/v1/messages \\
  -H "X-API-Key: your_api_key_here"`}
        </pre>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <a href="/docs/api/send-bulk-sms" className="block border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
          <h3 className="font-semibold text-gray-900 mb-2">Send Bulk SMS</h3>
          <p className="text-gray-600 text-sm">Learn how to send messages to multiple recipients efficiently</p>
        </a>

        <a href="/docs/webhooks" className="block border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
          <h3 className="font-semibold text-gray-900 mb-2">Set Up Webhooks</h3>
          <p className="text-gray-600 text-sm">Receive real-time notifications for message events</p>
        </a>

        <a href="/docs/guides/errors" className="block border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
          <h3 className="font-semibold text-gray-900 mb-2">Error Handling</h3>
          <p className="text-gray-600 text-sm">Learn how to handle errors and implement retry logic</p>
        </a>

        <a href="/docs/api/list-devices" className="block border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
          <h3 className="font-semibold text-gray-900 mb-2">Manage Devices</h3>
          <p className="text-gray-600 text-sm">Monitor and control your SMS gateway devices</p>
        </a>
      </div>

      <div className="not-prose bg-blue-50 border-l-4 border-blue-600 p-4 my-6">
        <p className="text-sm text-blue-900">
          <strong>Need help?</strong> Check out our complete <a href="/docs/api/send-sms" className="underline">API Reference</a> or contact support at support@textredge.com
        </p>
      </div>
    </div>
  )
}
